import React from "react";
import PropTypes from "prop-types";
import styles from "../ArticleCard/index.module.scss";
import { omitText, Typography } from "@kohler-dsm-library/kohler-core";
import { renderRichText, applyLocaleToUrl } from "@/utils/formatters";
import { useRouter } from "next/router";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { hideContentforLocale } from "@/utils/helper";
import { getLinkTarget } from "@/utils/helper";

const ArticleCard = ({ articleData = {} }) => {
  const { locale } = useRouter();
  if (!articleData) return null;
  const {
    background,
    heading,
    subHeading,
    image,
    ctas = [],
    showVideo = false,
    videoUrl = "",
    borderLine = true,
    longVideoUrl = "",
    noDescription = false,
    headingColor,
    borderLineColor,
    headingWidth = "full-width",
    showReverse = false,
    showArrow = false,
    headingFontSize = "18",
    hideContentOn = "",
  } = articleData;

  const imageUrl = image?.fields?.imageUrl;
  const { asset = {} } = image?.fields || {};
  const { file = {} } = asset?.fields || {};
  const { url: assetUrl = "" } = file;
  const ctasArray = Array.isArray(ctas) ? ctas : [];

  const validBackgrounds = ["white", "black"];
  const bg = validBackgrounds.includes(background) ? background : "white";
  const isWhiteBg = bg === "white";
  const isBlackBg = bg === "black";
  const colorMap = {
    Blue: "#92d7e9",
    White: "#ffffff",
    Black: "#000000",
    Yellow: "#f2a054",
  };

  let showhideContent = hideContentforLocale(locale, hideContentOn);
  const contentHideClass = showhideContent ? "hidden" : "";

  const normalizedHeadingColor =
    headingColor && headingColor.trim() !== "" ? headingColor : null;

  const normalizedLineColor =
    borderLineColor && borderLineColor.trim() !== "" ? borderLineColor : null;

  let finalHeadingColor;

  if (isBlackBg) {
    if (normalizedHeadingColor && colorMap[normalizedHeadingColor]) {
      finalHeadingColor = colorMap[normalizedHeadingColor];
    } else {
      finalHeadingColor = "#ffffff";
    }
  } else {
    finalHeadingColor = "#232323";
  }
  let finalLineColor;

  if (isBlackBg) {
    if (normalizedLineColor && colorMap[normalizedLineColor]) {
      finalLineColor = colorMap[normalizedLineColor];
    } else {
      finalLineColor = "#ffffff";
    }
  }
  const description = articleData?.description
    ? {
        text: documentToReactComponents(articleData.description, {
          renderNode: {
            [BLOCKS.HEADING_1]: (node, children) => (
              <h1
                className="pb-[10px] whitespace-pre-wrap"
                style={{
                  color: finalHeadingColor,
                  fontSize: "35px",
                }}
              >
                {children}
              </h1>
            ),
            [BLOCKS.HEADING_2]: (node, children) => (
              <h2
                className="pb-[10px] whitespace-pre-wrap"
                style={{
                  color: finalHeadingColor,
                  fontSize: "30px",
                }}
              >
                {children}
              </h2>
            ),
            [BLOCKS.HEADING_3]: (node, children) => (
              <h3
                className="pb-[10px] whitespace-pre-wrap"
                style={{
                  color: finalHeadingColor,
                  fontSize: "24px",
                }}
              >
                {children}
              </h3>
            ),
            [BLOCKS.HEADING_4]: (node, children) => (
              <h4
                className="pb-[10px] whitespace-pre-wrap"
                style={{
                  color: finalHeadingColor,
                  fontSize: "21px",
                }}
              >
                {children}
              </h4>
            ),
            [BLOCKS.HEADING_5]: (node, children) => (
              <h5
                className="pb-[10px] whitespace-pre-wrap"
                style={{
                  color: finalHeadingColor,
                  fontSize: "18px",
                }}
              >
                {children}
              </h5>
            ),
            [BLOCKS.HEADING_6]: (node, children) => (
              <h6
                className="pb-[10px] whitespace-pre-wrap"
                style={{
                  color: finalHeadingColor,
                  fontSize: "14px",
                }}
              >
                {children}
              </h6>
            ),

            [BLOCKS.PARAGRAPH]: (node, children) => (
              <p className="whitespace-pre-wrap">{children}</p>
            ),

            [BLOCKS.UL_LIST]: (node, children) => (
              <ul style={{ marginLeft: "20px", marginTop: "10px" }}>
                {children}
              </ul>
            ),

            [BLOCKS.LIST_ITEM]: (node, children) => (
              <li style={{ marginBottom: "8px" }}>{children}</li>
            ),

            [INLINES.HYPERLINK]: (node, children) => (
              <a
                href={applyLocaleToUrl(node.data.uri, locale)}
                target={node.data.uri.includes("http") ? "_blank" : "_self"}
                className="text-[#337ab7] hover:!text-[#23527c]"
                style={{
                  textDecoration: "none",
                  fontWeight: "500",
                  fontFamily: "HelveticaRoman, Helvetica, sans-serif",
                  letterSpacing: "normal",
                }}
              >
                {children}
              </a>
            ),

            renderMark: {
              bold: text => <strong>{text}</strong>,
              italic: text => <em>{text}</em>,
            },

            [BLOCKS.OL_LIST]: (node, children) => (
              <ol
                style={{
                  listStyleType: "decimal",
                }}
              >
                {children}
              </ol>
            ),
          },
        }),
      }
    : null;
  const hasImage = Boolean(imageUrl || assetUrl);
  const hasHeading = Boolean(heading);
  const hasSubHeading = Boolean(subHeading);
  const hasDescription = Boolean(description);
  const imageSrc = imageUrl || assetUrl;

  const onlyImageMode =
    hasImage && !hasHeading && !hasSubHeading && !hasDescription;

  return (
    <div className={`${contentHideClass}`}>
      <section
        className={`${styles.articleSection}  
        }  ${isWhiteBg ? styles.whiteBackground : ""}`}
      >
        {!onlyImageMode && heading && headingWidth === "full-width" && (
          <div className={styles.articleHeadingContainer}>
            <h2
              className={styles.articleHeadingFullWidth}
              style={{
                color: finalHeadingColor,
                fontSize: `${headingFontSize}px`,
              }}
            >
              {heading}
            </h2>
          </div>
        )}
        <div
          className={`${styles.articleContainer}  ${
            noDescription ? styles.noDescriptionarticleSection : ""
          }  ${
            !hasImage ? styles.noImageContainer : ""
          } ${!hasImage && !hasHeading && !hasSubHeading ? styles.onlyRichDescription : ""} ${
            !onlyImageMode && heading && headingWidth === "full-width"
              ? styles.noTopPadding
              : ""
          } ${showReverse ? styles.reverseContainer : ""}`}
        >
          {onlyImageMode && (hasImage || showVideo) && (
            <div
              className={
                showVideo
                  ? `${styles.onlyVideo}`
                  : `${styles.onlyImageContainer}`
              }
            >
              <div
                className={
                  showVideo
                    ? `${styles.onlyVideoWrapper}`
                    : `${styles.fullImageWrapper}`
                }
              >
                {onlyImageMode && !showVideo && (
                  <ImageSection
                    imageAlt={image?.fields?.altText || "Feature Image"}
                    imageSrc={imageSrc}
                    links={image?.fields?.externalUrl}
                    noDescription={noDescription}
                    locale={locale}
                  />
                )}
                {showVideo && (
                  <div className={styles["video-wrapper"]}>
                    <iframe
                      className={styles["iframe-fullVideo"]}
                      title="Article video content"
                      src={videoUrl || longVideoUrl}
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
              {ctasArray.length > 0 && (
                <div className={styles.ctaContainer}>
                  {ctasArray.map((cta, index) => {
                    const { label, url, mediaUrl, ctaType, openerType } =
                      cta?.fields || {};

                    if (!label) return null;

                    if (ctaType === "Button") {
                      return (
                        <li key={index}>
                          <a
                            key={index}
                            href={applyLocaleToUrl(url || mediaUrl, locale)}
                            target={
                              openerType === "New Tab" ? "_blank" : "_self"
                            }
                            className={`${styles.articlePagesTextLink} ${
                              isWhiteBg
                                ? styles.whiteBgButton
                                : styles.blackBgButton
                            }`}
                          >
                            {label}
                          </a>
                        </li>
                      );
                    }
                    if (ctaType === "Text Link") {
                      return (
                        <li key={index}>
                          <a
                            key={index}
                            target={
                              openerType === "New Tab" ? "_blank" : "_self"
                            }
                            href={applyLocaleToUrl(url || mediaUrl, locale)}
                            className={`${styles.articleTextLink} ${
                              isWhiteBg
                                ? styles.textLinkBlackBg
                                : styles.textLinkWhiteBg
                            }`}
                          >
                            {label}
                          </a>
                        </li>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          )}

          {!onlyImageMode && (
            <>
              {(hasImage || showVideo) && (
                <div className={styles.articleImageWrapper}>
                  {hasImage && !showVideo && (
                    <ImageSection
                      imageAlt={image?.fields?.altText || "Feature Image"}
                      imageSrc={imageSrc}
                      links={image?.fields?.externalUrl}
                      noDescription={noDescription}
                      locale={locale}
                    />
                  )}
                  {showVideo && (
                    <iframe
                      className={styles["iframe-video"]}
                      title="Article video content"
                      src={videoUrl || longVideoUrl}
                      allowFullScreen
                    />
                  )}
                </div>
              )}
              <div className={`${styles.articleContent}`}>
                {heading && headingWidth === "default-width" && (
                  <h2
                    className={`${styles.articleHeading} ${showReverse ? styles.reverseContent : ""}`}
                    style={{
                      color: finalHeadingColor,
                      fontSize: `${headingFontSize}px`,
                    }}
                  >
                    {heading}
                  </h2>
                )}
                {subHeading && (
                  <h3
                    className={`${styles.articleSubHeading} ${showReverse ? styles.reverseContent : ""}`}
                    style={{ color: finalHeadingColor }}
                  >
                    {subHeading}
                  </h3>
                )}
                {description && (
                  <div className={showArrow ? styles.quoteContainer : ""}>
                    {showArrow && <span className={styles.openQuote}>“</span>}
                    <div className={showArrow ? styles.quoteContent : ""}>
                      <Typography
                        component="div"
                        textColor={isWhiteBg ? "#232323" : "#fff"}
                        {...omitText(description)}
                        className={styles.typographyContent}
                      >
                        {description.text}
                      </Typography>
                    </div>
                    {showArrow && <span className={styles.closeQuote}>”</span>}
                  </div>
                )}
                {ctasArray.length > 0 && (
                  <div className={styles.ctaContainer}>
                    {ctasArray.map((cta, index) => {
                      const {
                        label,
                        url,
                        mediaUrl,
                        ctaType,
                        openerType = "",
                      } = cta?.fields || {};

                      if (!label) return null;

                      if (ctaType === "Button") {
                        return (
                          <li key={index}>
                            <a
                              key={index}
                              href={applyLocaleToUrl(url || mediaUrl, locale)}
                              target={
                                openerType === "New Tab" ? "_blank" : "_self"
                              }
                              className={`${styles.articlePagesTextLink} ${
                                isWhiteBg
                                  ? styles.whiteBgButton
                                  : styles.blackBgButton
                              }`}
                            >
                              {label}
                            </a>
                          </li>
                        );
                      }
                      if (ctaType === "Text Link") {
                        return (
                          <li key={index}>
                            <a
                              key={index}
                              target={
                                openerType === "New Tab" ? "_blank" : "_self"
                              }
                              href={applyLocaleToUrl(url || mediaUrl, locale)}
                              className={`${styles.articleTextLink} ${
                                isWhiteBg
                                  ? styles.textLinkBlackBg
                                  : styles.textLinkWhiteBg
                              }`}
                            >
                              {label}
                            </a>
                          </li>
                        );
                      }

                      return null;
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.articleContainer}>
          {borderLine && (
            <div
              className={styles.cardDivider}
              style={
                finalLineColor
                  ? { borderBottom: `1px solid ${finalLineColor}` }
                  : {}
              }
            ></div>
          )}
        </div>
      </section>
    </div>
  );
};

const ImageSection = ({
  links = "",
  imageSrc = "",
  imageAlt = "",
  image = {},
  noDescription = false,
  locale = "",
}) => {
  const hasLink = links != null && links !== "";
  return hasLink ? (
    <a href={applyLocaleToUrl(links, locale)} target={getLinkTarget(links)}>
      <img
        src={imageSrc}
        alt={imageAlt}
        className={noDescription ? styles.imageWithNoDesc : styles.fullImage}
      />
    </a>
  ) : (
    <img
      src={imageSrc}
      alt={imageAlt}
      className={noDescription ? styles.imageWithNoDesc : styles.fullImage}
    />
  );
};

ArticleCard.propTypes = {
  articleData: PropTypes.shape({
    background: PropTypes.string,
    heading: PropTypes.string,
    subHeading: PropTypes.string,
    image: PropTypes.object,
    ctas: PropTypes.array,
    showVideo: PropTypes.bool,
    videoUrl: PropTypes.string,
    description: PropTypes.object,
  }),
};

export default ArticleCard;
